import { useState } from "react";
import {
  Grid,
  Typography,
  // TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { FormErrorMessage, TextField } from "@avenue-8/sidekick-ui";
import { FindListingsFiltersFormSchema } from "@cma/chat-widgets/find-listings-search/helpers/property-search-filters-form-helpers";
import {
  LOT_SIZE_MAX_VALUE,
  isNumberAllowed,
} from "@cma/chat-widgets/find-listings-search/helpers/property-search-filters-form-helpers";
import {
  ACRES_THRESHOLD_SQFT,
  convertAcresToSqft,
  convertSqftToAcres,
} from "@shared/utils/form-converters";

type LotSizeUnit = "sqft" | "acres";

const getMinMaxErrors = (
  minError: { message?: string } | undefined,
  maxError: { message?: string } | undefined
): string => {
  if (minError?.message) return minError.message;
  if (maxError?.message) return maxError.message;
  return " ";
};

const determineDefaultUnit = (sqft: string | undefined): LotSizeUnit => {
  if (!sqft) return "sqft";
  const numValue = parseFloat(sqft);
  if (isNaN(numValue)) return "sqft";
  return numValue >= ACRES_THRESHOLD_SQFT ? "acres" : "sqft";
};

const convertValueForDisplay = (
  sqft: string | undefined,
  unit: LotSizeUnit
): string => {
  if (!sqft) return "";
  const numValue = parseFloat(sqft);
  if (isNaN(numValue)) return "";
  if (unit === "acres") {
    const acres = convertSqftToAcres(numValue);
    return parseFloat(acres.toFixed(2)).toString();
  }
  return Math.round(numValue).toString();
};

const convertValueToSqft = (value: string, unit: LotSizeUnit): string => {
  const numValue = parseFloat(value);
  if (isNaN(numValue) || !value) return "";
  const sqftValue = unit === "acres" ? convertAcresToSqft(numValue) : numValue;
  return Math.round(sqftValue).toString();
};

export const LotSizeField = () => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FindListingsFiltersFormSchema>();

  const lotSizeMin = watch("lotSizeMin");
  const lotSizeMax = watch("lotSizeMax");

  const [unit, setUnit] = useState<LotSizeUnit>(() => {
    const minUnit = determineDefaultUnit(lotSizeMin);
    const maxUnit = determineDefaultUnit(lotSizeMax);
    return lotSizeMin ? minUnit : maxUnit;
  });

  const handleMinValueChange = (value: string) => {
    const sqftValue = convertValueToSqft(value, unit);
    setValue("lotSizeMin", sqftValue);
  };

  const handleMaxValueChange = (value: string) => {
    const sqftValue = convertValueToSqft(value, unit);
    setValue("lotSizeMax", sqftValue);
  };

  const handleUnitChange = (newUnit: LotSizeUnit) => {
    setUnit(newUnit);
  };

  return (
    <Grid container item xs={12} columnSpacing={2}>
      <Grid item xs={12} mb={1}>
        <Typography variant="subtitle1" fontWeight={700}>
          Lot Size
        </Typography>
      </Grid>

      <Grid container item spacing={2}>
        <Grid container item xs={12} sm={6} spacing={1}>
          <Grid item xs={8}>
            <Controller
              name="lotSizeMin"
              control={control}
              render={({ field: { ref } }) => (
                <NumericFormat
                  customInput={TextField}
                  label="Minimum"
                  placeholder="No Min"
                  hideHelperTextSpace
                  fullWidth
                  thousandSeparator=","
                  allowNegative={false}
                  decimalScale={unit === "acres" ? 2 : 0}
                  error={!!errors.lotSizeMin}
                  value={convertValueForDisplay(lotSizeMin, unit)}
                  onValueChange={(v) => handleMinValueChange(v.value)}
                  getInputRef={ref}
                  isAllowed={({ floatValue }) => {
                    if (!floatValue) return true;
                    const sqftValue = parseFloat(
                      convertValueToSqft(floatValue.toString(), unit)
                    );
                    return isNumberAllowed(sqftValue, LOT_SIZE_MAX_VALUE);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel>Unit</InputLabel>
              <Select
                value={unit}
                label="Unit"
                onChange={(e) =>
                  handleUnitChange(e.target.value as LotSizeUnit)
                }
              >
                <MenuItem value="sqft">Sq Ft</MenuItem>
                <MenuItem value="acres">Acres</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container item xs={12} sm={6} spacing={1}>
          <Grid item xs={8}>
            <Controller
              name="lotSizeMax"
              control={control}
              render={({ field: { ref } }) => (
                <NumericFormat
                  customInput={TextField}
                  label="Maximum"
                  placeholder="No Max"
                  hideHelperTextSpace
                  fullWidth
                  thousandSeparator=","
                  allowNegative={false}
                  decimalScale={unit === "acres" ? 2 : 0}
                  error={!!errors.lotSizeMax}
                  value={convertValueForDisplay(lotSizeMax, unit)}
                  onValueChange={(v) => handleMaxValueChange(v.value)}
                  getInputRef={ref}
                  isAllowed={({ floatValue }) => {
                    if (!floatValue) return true;
                    const sqftValue = parseFloat(
                      convertValueToSqft(floatValue.toString(), unit)
                    );
                    return isNumberAllowed(sqftValue, LOT_SIZE_MAX_VALUE);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel>Unit</InputLabel>
              <Select
                value={unit}
                label="Unit"
                onChange={(e) =>
                  handleUnitChange(e.target.value as LotSizeUnit)
                }
              >
                <MenuItem value="sqft">Sq Ft</MenuItem>
                <MenuItem value="acres">Acres</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <FormErrorMessage>
          {getMinMaxErrors(errors.lotSizeMin, errors.lotSizeMax)}
        </FormErrorMessage>
      </Grid>
    </Grid>
  );
};
